import { useCallback, useEffect, useRef, useState } from 'react';
import type { PendingNotificationsResponse } from '@find-my-part/types';
import { adminApi } from '../api/client';

export const WORKBENCH_STALE_AFTER_MS = 5 * 60 * 1000;

export type WorkbenchFreshness = 'fresh' | 'stale' | 'unavailable';

export interface PendingWorkSnapshot {
  data: PendingNotificationsResponse | null;
  freshness: WorkbenchFreshness;
  refreshing: boolean;
  announcement: string;
  refresh: () => Promise<void>;
  announcementRevision: number;
}

function hasUsableTimestamp(data: PendingNotificationsResponse): boolean {
  return Number.isFinite(Date.parse(data.asOf));
}

/** Shared pending-work read for Overview tiles and nav badges (ADR-025 B-3). */
export function usePendingWork(token: string | null, enabled = true): PendingWorkSnapshot {
  const [data, setData] = useState<PendingNotificationsResponse | null>(null);
  const dataRef = useRef<PendingNotificationsResponse | null>(null);
  const [freshness, setFreshness] = useState<WorkbenchFreshness>('unavailable');
  const [refreshing, setRefreshing] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [announcementRevision, setAnnouncementRevision] = useState(0);
  const announce = useCallback((message: string): void => {
    setAnnouncement(message);
    setAnnouncementRevision((revision) => revision + 1);
  }, []);
  const requestGenerationRef = useRef(0);



  const refresh = useCallback(async (): Promise<void> => {
    if (!token || !enabled) return;
    const generation = ++requestGenerationRef.current;

    setRefreshing(true);
    try {
      const response = await adminApi.notifications.getPending(token);
      if (!response.success || !hasUsableTimestamp(response.data)) {
        throw new Error('Pending-work response unavailable');
      }
      if (generation !== requestGenerationRef.current) return;


      dataRef.current = response.data;
      setData(response.data);
      const stale = Date.now() - Date.parse(response.data.asOf) >= WORKBENCH_STALE_AFTER_MS;
      setFreshness(stale ? 'stale' : 'fresh');
      announce(
        stale
          ? 'Pending work counts loaded, but they are stale.'
          : 'Pending work counts updated.'
      );
    } catch {
      if (generation !== requestGenerationRef.current) return;
      const hasLastGood = dataRef.current !== null;
      setFreshness(hasLastGood ? 'stale' : 'unavailable');
      announce(
        hasLastGood
          ? 'Pending work refresh failed. Last known counts are now stale.'
          : 'Pending work counts are unavailable. Retry the refresh.'
      );
    } finally {
      if (generation === requestGenerationRef.current) {
        setRefreshing(false);
      }
    }
  }, [announce, enabled, token]);

  useEffect(() => {
    if (!enabled || !token) {
      requestGenerationRef.current += 1;
      dataRef.current = null;
      setData(null);
      setFreshness('unavailable');
      setRefreshing(false);
      setAnnouncement('');
      setAnnouncementRevision(0);
      return;
    }
    dataRef.current = null;
    setData(null);
    setFreshness('unavailable');
    setAnnouncement('');
    setAnnouncementRevision(0);
    void refresh();
  }, [enabled, refresh, token]);

  useEffect(() => {
    if (!data || freshness !== 'fresh') return;
    const remaining = WORKBENCH_STALE_AFTER_MS - (Date.now() - Date.parse(data.asOf));
    if (remaining <= 0) {
      setFreshness('stale');
      announce('Pending work counts are stale. Refresh to update them.');
      return;
    }
    const timer = window.setTimeout(() => {
      setFreshness('stale');
      announce('Pending work counts are stale. Refresh to update them.');
    }, remaining);
    return () => window.clearTimeout(timer);
  }, [announce, data, freshness]);

  return { data, freshness, refreshing, announcement, announcementRevision, refresh };
}
